import { Input } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import type { BusinessToolTestResponse } from '@circuitry-ai/doc-data';

export type BusinessToolTestPanelProps = Readonly<{
  testParams: Record<string, string>;
  testResult: BusinessToolTestResponse | null;
  isTesting: boolean;
  onTestParamChange: (paramName: string, value: string) => void;
  onTest: () => void;
}>;

export function BusinessToolTestPanel({
  testParams,
  testResult,
  isTesting,
  onTestParamChange,
  onTest,
}: BusinessToolTestPanelProps) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primary bg-primary/10 p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-foreground">Test tool</p>
          <p className="text-xs text-default-500">
            Enter sample parameter values and run a live test.
          </p>
        </div>
        <CtyButton
          size="sm"
          color="primary"
          variant="flat"
          isLoading={isTesting}
          onPress={onTest}
          startContent={<Icon icon="mdi:play" width={14} height={14} />}
        >
          Test
        </CtyButton>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {Object.keys(testParams).map((paramName) => (
          <Input
            key={paramName}
            size="sm"
            label={paramName}
            labelPlacement="outside"
            variant="bordered"
            value={testParams[paramName] ?? ''}
            onValueChange={(value) => onTestParamChange(paramName, value)}
          />
        ))}
      </div>
      {testResult ? (
        <div className="rounded-lg border border-divider bg-content1 p-3 flex flex-col gap-2 text-xs font-mono whitespace-pre-wrap break-words">
          <p>Success: {String(testResult.success ?? '—')}</p>
          <p>Status code: {String(testResult.status_code ?? '—')}</p>
          <p>Execution time (ms): {String(testResult.execution_time_ms ?? '—')}</p>
          <div>
            <p className="font-sans text-default-500 mb-1">Processed response</p>
            {JSON.stringify(testResult.processed_response ?? null, null, 2)}
          </div>
          <div>
            <p className="font-sans text-default-500 mb-1">Transformation log</p>
            {JSON.stringify(testResult.transformation_log ?? null, null, 2)}
          </div>
        </div>
      ) : null}
    </div>
  );
}
